import { api } from '@cwd/lib/main';

export interface PrePaidBalance {
    AccountBalance: number | null;
    SubscriptionBalance: number | null;
    FullBalance: number | null;
    CurrencyCode: string;
    CreditLimit: number;
}

export async function getPrePaidBalance(): Promise<PrePaidBalance> {
    return api.get('/Accounts/Current/PrePaidBalance');
}

export type TopUpTarget = 'ACCOUNT' | 'SUBSCRIPTION';

export type SmsTopUpRefusal =
    | 'None'
    | 'NotChargebeeOwned'
    | 'AccountInactive'
    | 'BelowMinimum'
    | 'AboveMaximum'
    | 'InvalidRequest'
    | 'SubscriptionNotInGoodStanding'
    | 'NotTrustedForInvoice'
    | 'BillingAddressIncomplete'
    | 'BillingAddressLocked'
    | 'PaymentNotAuthorized'
    | 'NotPaymentAuthorized'
    | 'AboveInvoiceLimit'
    | 'OverdueInvoice'
    | 'InvoiceDetailsMissing'
    | 'InvoiceBusy';

// The api client rejects with { status, message: body }; a 409 body is { Refusal }.
export interface ApiError {
    status?: number;
    message?: string;
}

export function refusalOf(error: ApiError | undefined): SmsTopUpRefusal | null {
    if (error?.status !== 409) return null;
    try {
        return (JSON.parse(error.message ?? '') as { Refusal?: SmsTopUpRefusal }).Refusal ?? null;
    } catch {
        return null;
    }
}

export interface SmsTopUpOffer {
    Refusal: SmsTopUpRefusal;
    Site: string | null;
    PublishableKey: string | null;
    CurrencyCode: string | null;
    Minimum: number | null;
    Maximum: number | null;
    CanInvoice: boolean;
    // An administrator or "Betalningsbehörig" (SMS top-up on invoice, R1–R2).
    CanPay: boolean;
    // An administrator: may complete the customer's missing billing address, card path only (D2; the invoice path needs it complete, R17); others are told to ask one.
    CanCompleteAddress: boolean;
}

// Own billing details for one invoice (R15): the customer's country is always used, so there is no country field.
export interface SmsTopUpInvoiceRecipient {
    Company: string;
    FirstName: string | null;
    LastName: string | null;
    Line1: string;
    Line2: string | null;
    City: string;
    Zip: string;
    Email: string | null;
}

// Amount is net (excl. VAT). RequestId is the idempotency key: new on amount, target or reference change, after a 502, after a completed top-up.
export interface SmsTopUpRequest {
    Amount: number;
    Target: TopUpTarget;
    RequestId: string;
    Reference?: string | null;
    Recipient?: SmsTopUpInvoiceRecipient | null;
}

export async function getSmsTopUpOffer(): Promise<SmsTopUpOffer> {
    return api.get('/SmsTopUp');
}

// SMS top-up on invoice — the company's credit (decided 2026-10-28): limit, used and left in the currency's units, excl. VAT.
// Blocked: why "Lägg på faktura" is refused right now; None when at least the minimum fits.
export interface SmsTopUpCreditStatus {
    Blocked: SmsTopUpRefusal;
    CurrencyCode: string | null;
    Limit: number | null;
    Used: number;
    Available: number | null;
    RetrievedAt: string;
}

// The account's administrators only (403 otherwise).
export async function getSmsTopUpInvoiceCredit(): Promise<SmsTopUpCreditStatus> {
    return api.get('/SmsTopUp/InvoiceCredit');
}

export async function addSmsTopUpToInvoice(request: SmsTopUpRequest): Promise<void> {
    return api.post('/SmsTopUp/Invoice', request);
}

// The own checkout (card 5.3): the customer's billing address, completed once when it is missing, and the card payment.
export interface BillingAddress {
    FirstName: string | null;
    LastName: string | null;
    Company: string | null;
    Line1: string | null;
    Line2: string | null;
    City: string | null;
    Zip: string | null;
    Country: string | null;
}

export interface SmsTopUpBillingAddress {
    Refusal: SmsTopUpRefusal;
    Address: BillingAddress | null;
    Complete: boolean;
}

// PaymentIntent is Chargebee's own payment_intent object, as the Payment Component takes it.
export interface SmsTopUpCardStart {
    PaymentIntent: { id: string; amount: number; currency_code: string };
    NetInMinorUnits: number;
    TotalInMinorUnits: number;
}

export async function getSmsTopUpBillingAddress(): Promise<SmsTopUpBillingAddress> {
    return api.get('/SmsTopUp/BillingAddress');
}

export async function saveSmsTopUpBillingAddress(address: BillingAddress): Promise<void> {
    return api.put('/SmsTopUp/BillingAddress', address);
}

export interface SmsTopUpEstimate {
    NetInMinorUnits: number;
    TotalInMinorUnits: number;
}

// The invoice's total incl. VAT before "Lägg på faktura" (taxed by the customer's billing address).
export async function estimateSmsTopUp(request: SmsTopUpRequest): Promise<SmsTopUpEstimate> {
    return api.post('/SmsTopUp/Estimate', request);
}

export async function startSmsTopUpCard(request: SmsTopUpRequest): Promise<SmsTopUpCardStart> {
    return api.post('/SmsTopUp/Card', request);
}

export async function confirmSmsTopUpCard(request: SmsTopUpRequest, paymentIntentId: string): Promise<void> {
    return api.post('/SmsTopUp/Card/Confirm', { ...request, PaymentIntentId: paymentIntentId });
}
